'use client';

import { Builder } from '@builder.io/react';
import Close from '@hc-frontend/core-icons/kit/close';
import { DialogCloseButton, Link } from '@hc-frontend/core-ui';
import { toUSPhone } from '@hc-frontend/core-utils-format';
import { CallCta } from '@hc-frontend/shells-shared';
import { useEffect } from 'react';

import { AnimatedPhoneIcon } from '../../atoms/animated-phone-icon/animated-phone-icon';
import { ConnectMeButton } from '../../atoms/connect-me-button';
import { useUccModalDataContext } from './modal-data-provider';
import { useModalCommonUtils } from './use-modal-common-utils';

type UccModalContentMultipleOptionsProps = {
  showModalInEditor?: boolean;
  image?: string;
  heading?: string;
  partnerName?: string;
  buttonText?: string;
};

export function UccModalContentMultipleOptions({
  showModalInEditor,
  image,
  heading,
  partnerName,
  buttonText,
}: UccModalContentMultipleOptionsProps) {
  const headingParts = heading?.split('{partner_name}');
  const isBuilderEditing = Builder.isEditing;
  const finalButtonText = buttonText ? buttonText : 'Start Call!';

  const {
    phoneNumber,
    primaryCampaign,
    secondaryCampaign,
    metricName,
    experience,
    sessionId,
    product,
  } = useUccModalDataContext();

  const {
    isOpen,
    isInitCall,
    internalRef,
    connectMeButtonId,
    clickToCallButtonId,
    clickToCallLinkId,
    connectMeDestinationPhoneNumber,
    handleOpen,
    handleClose,
    handleOnClickBackdrop,
    handleOnClickClose,
    handleOnClickConnectMe,
  } = useModalCommonUtils();

  useEffect(() => {
    if (!isBuilderEditing) {
      return;
    }

    if (showModalInEditor) {
      if (!isOpen) {
        handleOpen();
      }
      return;
    }

    if (isOpen) {
      handleClose({
        elementId: 'connect-me-modal-editor-toggle',
        action: 'Editor Toggle Close',
      });
    }
  }, [handleClose, handleOpen, isBuilderEditing, isOpen, showModalInEditor]);

  if (!primaryCampaign || !secondaryCampaign) {
    return null;
  }

  return (
    <div className="hc-imposter-layout">
      <dialog
        aria-modal="true"
        ref={internalRef}
        onClick={handleOnClickBackdrop}
        className="max-w-[500px] overflow-visible rounded-lg bg-gray-0 shadow-lg backdrop:bg-[rgba(0,0,0,0.65)]"
      >
        <div className="relative flex w-full justify-center overflow-visible">
          {image ? (
            <img
              src={image}
              alt="Partner logo"
              width={300}
              height={37}
              className="px-24-24 pt-48-48"
            />
          ) : null}
          <DialogCloseButton
            className="absolute right-16-16 top-16-16 outline-none"
            onClick={handleOnClickClose}
          >
            <Close className="size-16-16 text-gray-200" />
          </DialogCloseButton>
        </div>

        <div className="px-24-24 py-32-32 text-center text-text-950">
          {headingParts ? (
            <h1 className="hc-typography--heading-three mb-24-32 text-balance">
              {headingParts.map((part, index) => (
                <span key={`heading-part-${index}`}>
                  {part}
                  {index < headingParts.length - 1 ? (
                    <span className="text-secondary-900">{partnerName}</span>
                  ) : null}
                </span>
              ))}
            </h1>
          ) : null}

          {phoneNumber ? (
            <>
              {!isInitCall ? (
                <p
                  className="hc-typography--paragraph-md mb-32-32 text-balance"
                  data-testid="initial-connect-me-message"
                >
                  To connect, click{' '}
                  <span className="font-bold">Connect Me!</span> and pick up the
                  phone when it rings.
                </p>
              ) : null}

              <div
                className={`${isInitCall ? 'border border-dashed border-gray-50 bg-primary-background-50 p-12-12' : ''} w-full rounded-md`}
              >
                {connectMeDestinationPhoneNumber && (
                  <ConnectMeButton
                    className="w-full"
                    id={connectMeButtonId}
                    metricName={metricName}
                    variant="primary-card"
                    type="button"
                    experience={experience}
                    phoneNumber={phoneNumber}
                    destinationPhoneNumber={connectMeDestinationPhoneNumber}
                    callType={primaryCampaign.callType}
                    placement={primaryCampaign.placement}
                    sessionId={sessionId}
                    product={product}
                    isSendingDisplayMetric={false}
                    onClick={handleOnClickConnectMe}
                    advertiser={primaryCampaign.advertiser}
                  >
                    <p className="flex gap-8-8 justify-center items-center mx-auto">
                      <AnimatedPhoneIcon />
                      <span className="mx-auto">Connect Me!</span>
                    </p>
                  </ConnectMeButton>
                )}
              </div>
            </>
          ) : (
            <>
              <p className="hc-typography--paragraph-md mb-32-32 text-balance">
                To speak with a licensed insurance agent, click{' '}
                <span className="font-bold">{finalButtonText}</span>
              </p>

              <CallCta
                id={clickToCallButtonId}
                phoneCampaign={secondaryCampaign}
                className="flex w-full flex-wrap items-center justify-center gap-12-12"
              >
                <p className="flex gap-8-8 justify-center items-center mx-auto">
                  <AnimatedPhoneIcon />
                  <span className="mx-auto">{finalButtonText}</span>
                </p>
              </CallCta>
            </>
          )}

          <p className="mt-8-8">
            Or dial at{' '}
            <Link
              className="font-bold"
              href={secondaryCampaign.href}
              id={clickToCallLinkId}
              data-call-call-type={secondaryCampaign.callType}
              data-call-placement={secondaryCampaign.placement}
              data-call-product={secondaryCampaign.product}
              data-call-phone={secondaryCampaign.href}
              data-call-advertiser={secondaryCampaign.advertiser}
            >
              {toUSPhone(secondaryCampaign.phoneNumber)}
            </Link>
          </p>
        </div>
      </dialog>
    </div>
  );
}
